import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../providers/auth-providers';
import { getExperiment } from '../../../statsig/statsig-client';
import analytics from '../../utils/analytics';
import Logger from '../../../logger';
import { sentryCaptureException } from '../../utils/sentry/sentry-client';
import { DynamicConfig } from '@atlaskit/feature-gate-js-client';

export interface ExperimentWrapperProps {
    experimentName: string;
    experimentParameter: string;
    defaultValues: Record<string, any>;
    customAttributes?: Record<string, any>;
    /** In the format of componentName: '<ComponentName>' for better Sentry logging */
    customErrorAttributes?: Record<string, any>;
    children: React.ReactNode;
    source: string;
}

let experimentResult: DynamicConfig;

export const ExperimentWrapper = ({
    experimentName,
    experimentParameter,
    defaultValues,
    customAttributes,
    customErrorAttributes,
    children,
    source
}: ExperimentWrapperProps) => {
    const [shouldRenderFeature, setShouldRenderFeature] = useState(false);
    const { aaId } = useAuth();

    const sendExperimentEvent = useCallback(
        (experimentResult: any, aaId: string) => {
            const eventMetadata = {
                action: 'evaluated',
                actionSubject: experimentName,
                actionSubjectId: `${experimentName} evaluated`
            };

            const attributes = {
                ...experimentResult,
                ...customAttributes,
                aaId,
                experimentName
            };

            analytics.sendGASV3OperationalEvent(eventMetadata, attributes);
        },
        [customAttributes, experimentName]
    );

    const isFeatureEnabled = useCallback(
        async (aaId: string) => {
            const identifiers = {
                atlassianAccountId: aaId
            };

            experimentResult = await getExperiment(
                experimentName,
                identifiers,
                customAttributes
            );

            sendExperimentEvent(experimentResult, aaId);

            return experimentResult.getValue(
                experimentParameter,
                defaultValues
            );
        },
        [
            defaultValues,
            experimentName,
            experimentParameter,
            sendExperimentEvent,
            customAttributes
        ]
    );

    useEffect(() => {
        if (aaId) {
            isFeatureEnabled(aaId)
                // @ts-ignore
                .then((isEnabled: boolean) => {
                    setShouldRenderFeature(!!isEnabled);

                    const eventMetadata = {
                        action: 'exposed',
                        actionSubject: 'feature',
                        actionSubjectId: `${source} feature exposed`
                    };

                    const attributes = {
                        isEnabled,
                        ...customAttributes,
                        ...experimentResult,
                        aaId
                    };

                    /** Sending feature exposure event */
                    analytics.sendGASV3OperationalEvent(
                        eventMetadata,
                        attributes
                    );
                })
                .catch((error) => {
                    Logger.warn(
                        {
                            error
                        },
                        `Error evaluating experiment ${experimentName}`
                    );
                    sentryCaptureException({
                        exception: {
                            ...customErrorAttributes,
                            error
                        }
                    });
                    setShouldRenderFeature(false);
                });
        } else {
            setShouldRenderFeature(false);
        }
    }, [
        aaId,
        isFeatureEnabled,
        experimentName,
        customErrorAttributes,
        customAttributes,
        source
    ]);

    return shouldRenderFeature && <>{children}</>;
};
